import { useEffect, useId, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, MailCheck, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { magicLinkSchema, normaliseAuthError } from './loginSchema';
import type { MagicLinkFormValues } from './loginSchema';

const inputBase =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ' +
  'placeholder:text-muted-foreground ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

export function MagicLinkTab() {
  const emailErrorId = useId();
  const formErrorId = useId();
  const formErrorRef = useRef<HTMLParagraphElement>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<MagicLinkFormValues>({
    resolver: zodResolver(magicLinkSchema),
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (errors.root) formErrorRef.current?.focus();
  }, [errors.root]);

  const onSubmit = async (values: MagicLinkFormValues) => {
    const { error: authError } = await supabase.auth.signInWithOtp({
      email: values.email,
      options: {
        // Supabase appends #access_token=… on success; SDK auto-detects.
        emailRedirectTo: `${window.location.origin}/auth`,
      },
    });
    if (authError) {
      setError('root', { message: normaliseAuthError(authError.message) });
      return;
    }
    setSentTo(values.email);
  };

  if (sentTo) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="space-y-3 rounded-md border border-border bg-accent/40 p-4 text-sm"
      >
        <div className="flex items-center gap-2 text-foreground">
          <MailCheck className="h-5 w-5 text-primary" aria-hidden="true" />
          <span className="font-medium">Check your inbox</span>
        </div>
        <p className="text-muted-foreground">
          We sent a sign-in link to <span className="font-medium text-foreground">{sentTo}</span>.
          Click the link in the email to finish signing in. The link expires in 1 hour.
        </p>
        <button
          type="button"
          onClick={() => {
            setSentTo(null);
          }}
          className="text-xs font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:rounded"
        >
          Use a different email
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      aria-label="Sign in with magic link"
      aria-busy={isSubmitting}
      noValidate
      className="space-y-4"
    >
      {errors.root && (
        <p
          id={formErrorId}
          ref={formErrorRef}
          role="alert"
          tabIndex={-1}
          aria-live="assertive"
          className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive focus-visible:outline-none"
        >
          {errors.root.message}
        </p>
      )}

      <div className="space-y-1">
        <label htmlFor="email-magic" className="text-sm font-medium text-foreground">
          Email
        </label>
        <input
          id="email-magic"
          type="email"
          autoComplete="email"
          aria-required="true"
          aria-invalid={errors.email ? 'true' : 'false'}
          aria-describedby={errors.email ? emailErrorId : undefined}
          placeholder="your@email.com"
          className={cn(inputBase, errors.email && 'border-destructive')}
          {...register('email')}
        />
        {errors.email && (
          <p id={emailErrorId} role="alert" className="text-xs text-destructive">
            {errors.email.message}
          </p>
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        We&rsquo;ll email you a one-time sign-in link. No password needed.
      </p>

      <button
        type="submit"
        disabled={isSubmitting}
        aria-disabled={isSubmitting}
        className={cn(
          'flex w-full items-center justify-center gap-2 rounded-md px-4 py-2',
          'bg-primary text-sm font-medium text-primary-foreground',
          'hover:bg-primary/90 transition',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        {isSubmitting ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Send className="h-4 w-4" aria-hidden="true" />
        )}
        {isSubmitting ? 'Sending…' : 'Send magic link'}
      </button>
    </form>
  );
}
